/* eslint-disable camelcase */
import {Carousel} from 'extensions/ui-components/x-carousel';
import 'extensions/ui-components/x-loader';
import {CategoryProductListController, LazyLoadController, SalesBrandsController} from 'extensions/ui-controllers';
import {defineCustomElement} from 'extensions/utils';
import {html} from 'lit';
import {ref} from 'lit/directives/ref.js';

const IMAGE_TYPE = 'main';
const IMAGE_HEIGHT = 234;
const IMAGE_WIDTH = 234;
const IMAGE_SIZE = `${IMAGE_HEIGHT}x${IMAGE_WIDTH}`;

export default class CategoryProductCarousel extends Carousel {
	static get properties () {
		return {
			...super.properties,
			categoryCode: {
				attribute: 'category-code',
				type: String
			},
			sort: {
				type: String
			}
		};
	}

	/**
	 * List of brands that are ok for sale price to be shown
	 * @type {string[]}
	 */
	saleBrands = [];

	constructor () {
		super();

		// Set default options
		this.categoryCode = '';

		// Create controllers
		this.lazyLoadController = new LazyLoadController(this, this.onLazy, {
			rootMargin: '50px 0px 0px 0px'
		});
		this.salesBrandsController = new SalesBrandsController(this);
	}

	async onLazy () {
		this.saleBrands = await this.salesBrandsController.getSalesBrands();

		// Get products
		this.categoryProductListController.getProducts();
	}

	firstUpdated () {
		// Create controller on first updated because we need the passed properties
		this.categoryProductListController = new CategoryProductListController(this, {
			categoryCode: this.categoryCode,
			count: this.slidesPerView * 4,
			filters: [
				{
					name: 'imagetypes',
					value: {
						sizes: [IMAGE_SIZE],
						types: [IMAGE_TYPE]
					}
				}
			],
			onDemandColumns: [
				'sale_price',
				'CustomField_Values:customfields:brand',
				'CustomField_Values:customfields:has_metal_options',
				'CustomField_Values:customfields:incart_pricing',
				'CustomField_Values:customfields:product_flag',
				'CustomField_Values:customfields:starting_at'
			],
			sort: this.sort
		});
	}

	/**
	 * @param {import('extensions/ui-controllers').MivaProduct} product
	 */
	renderProduct (product) {
		return html`
			<x-card type="product">
				<x-product-card
					brand="${product?.CustomField_Values?.customfields?.brand}"
					code="${product.code}"
					flag="${product?.CustomField_Values?.customfields?.product_flag}"
					?discounted-brand="${product?.CustomField_Values?.customfields?.brand && window.mivaJS?.discounted_brands?.includes?.(product.CustomField_Values.customfields.brand)}"
					formatted-price="${product.formatted_price}"
					formatted-retail="${product.formatted_retail}"
					gtm-list="${this.categoryCode}-carousel"
					?has-metal-options="${product?.CustomField_Values?.customfields?.has_metal_options}"
					image-height="${IMAGE_HEIGHT}"
					image-url="${product?.imagetypes[IMAGE_TYPE]?.sizes[IMAGE_SIZE]?.url}"
					image-width="${IMAGE_WIDTH}"
					?incart-pricing="${product?.CustomField_Values?.customfields?.incart_pricing}"
					name="${product.name}"
					price="${product.price}"
					retail="${product.retail}"
					sale-brands="${JSON.stringify(this.saleBrands)}"
					starting-at="${product?.CustomField_Values?.customfields?.starting_at}"
					url="${product.url}"
				></x-product-card>
			</x-card>
		`;
	}

	renderSlides () {
		return html`
			<slot name="title-card"></slot>
			${this.categoryProductListController?.products?.map(this.renderProduct.bind(this))}
		`;
	}

	renderSlider () {
		return html`<x-slick-slider ${ref(this.sliderRef)} .options="${this.sliderOptions}">
			${this.renderSlides()}
		</x-slick-slider>`;
	}

	renderLoader () {
		return html`<x-loader aspect-ratio-desktop="1600/516" aspect-ratio-tablet="727/416" aspect-ratio="319/416"></x-loader>`;
	}

	render () {
		return html`
			${this.categoryProductListController?.loaded ? this.renderSlider() : this.renderLoader()}
		`;
	}
};

defineCustomElement('x-category-product-carousel', CategoryProductCarousel);
